import { Feature } from "geojson";
import GeoJsonLayerGroup, { GeoJsonLayerGroupOptions } from "./GeoJsonLayerGroup";
import { AddRemoveVisibilityTypes } from "features/map/types/featureLayer";
import { IconID } from "types/auth";
import { GeojsonLayerStyleOverwrites, SvgIconStyleObject } from "types/mapData";
import { FeatureStyleObject } from "types/firestoreFeatures";
import { existsInAnotherLayerGroup } from "./helperFunctions";

interface LayerControllerProps {
  map: L.Map;
  setSelectedFeature: (feature: null | any, originCaller?: string) => void;
  getSpeciesColor: (species: string, styleObject?: FeatureStyleObject) => string;
  getLineStyle: (
    name?: string,
    styleObject?: FeatureStyleObject
  ) => { color: string; dashArray: string | null; weight: number };
  getIcon: (iconId: IconID, svgStyleObject: SvgIconStyleObject) => string | null;
  getSpeciesNameAndAbbreviation: (species: string) => { name: string; abbreviation: string };
  getOverwrites?: () => GeojsonLayerStyleOverwrites | undefined;
  options?: any;
}

interface UpdateLayerFeatureProps {
  layerId: string;
  feature: Feature;
}

class LayerController {
  // --- Public parameters --- //

  // --- Private options --- //
  private _layers: { [key: string]: any } = {};
  private _map: L.Map;
  // --- Private functions --- //
  private _setSelectedFeature: (feature: null | any, originCaller?: string) => void;
  private _getSpeciesColor: (species: string) => string;
  private _getIcon: (iconId: IconID, svgStyleObject: SvgIconStyleObject) => string | null;
  private _getLineStyle: (
    name?: string,
    styleObject?: FeatureStyleObject
  ) => { color: string; dashArray: string | null; weight: number };
  private _getSpeciesNameAndAbbreviation: (species: string) => {
    name: string;
    abbreviation: string;
  };
  private _getOverwrites?: () => GeojsonLayerStyleOverwrites | undefined;
  // --- Constructor --- //
  constructor({
    map,
    setSelectedFeature,
    getSpeciesColor,
    getIcon,
    getLineStyle,
    getSpeciesNameAndAbbreviation,
    getOverwrites,
    options,
  }: LayerControllerProps) {
    this._map = map;
    // Call back functions
    this._setSelectedFeature = setSelectedFeature;
    this._getSpeciesColor = getSpeciesColor;
    this._getIcon = getIcon;
    this._getLineStyle = getLineStyle;
    this._getSpeciesNameAndAbbreviation = getSpeciesNameAndAbbreviation;
    this._getOverwrites = getOverwrites;
  }

  // --- Public functions --- //
  // Add layer (Full feature)
  public addLayer(layerId: string, options?: GeoJsonLayerGroupOptions) {
    let updatedLayers = { ...this._layers };
    updatedLayers[layerId] = new GeoJsonLayerGroup({
      name: layerId,
      map: this._map,
      setSelectedFeature: this._setSelectedFeature,
      getSpeciesColor: this._getSpeciesColor,
      getIcon: this._getIcon,
      getLineStyle: this._getLineStyle,
      getSpeciesNameAndAbbreviation: this._getSpeciesNameAndAbbreviation,
      getOverwrites: this._getOverwrites,
      options: options,
    });
    this._layers = updatedLayers;
  }
  // Remove layer
  public removeLayer(layerId: string) {
    let updatedLayers = { ...this._layers };
    delete updatedLayers[layerId];
    this._layers = updatedLayers;
  }

  public addLayerVisibility(layerId: string, type: AddRemoveVisibilityTypes) {
    if (!layerId) {
      if (!type) {
        Object.values(this._layers).forEach((lay: any) => lay.addLayerVisibility("all"));
        return;
      }
      Object.values(this._layers).forEach((lay: any) => lay.addLayerVisibility(type));
      return;
    }
    this._layers[layerId].addLayerVisibility(type);
    return;
  }

  public removeLayerVisibility(layerId: string, type: AddRemoveVisibilityTypes) {
    if (!layerId) {
      if (!type) {
        Object.values(this._layers).forEach((lay: any) => lay.removeLayerVisibility("all"));
        return;
      }
      Object.values(this._layers).forEach((lay: any) => lay.removeLayerVisibility(type));
      return;
    }
    this._layers[layerId].removeLayerVisibility(type);
    return;
  }

  // Reset layer
  public resetLayers(layerId?: string, type?: "all" | "points" | "lines" | "polygons" | "labels") {
    if (!layerId) {
      if (!type) {
        Object.values(this._layers).forEach((lay: any) => lay.resetLayers("all"));
        return;
      }
      Object.values(this._layers).forEach((lay: any) => lay.resetLayers(type));
      return;
    }
    this._layers[layerId].resetLayers(type);
    return;
  }
  // Get layers
  public getLayers() {
    return this._layers;
  }

  public getActiveLayers(layerId?: string) {
    if (!layerId) {
      return Object.values(this._layers).map((lay: any) => lay.getActiveLayers());
    }
    return this._layers[layerId].getActiveLayers();
  }
  public getPolygonFeatureLayers(layerId: string): L.Layer[] {
    return this._layers[layerId]?.getPolygonFeatureLayers() ?? [];
  }
  // Update layer feature
  public updateFeature({ layerId, feature }: UpdateLayerFeatureProps): void | Error {
    try {
      // Check globally if feature already exist on map but in another layer
      const exists = existsInAnotherLayerGroup(this._map, feature.properties?.id || "", layerId);
      if (feature.properties?.id && !feature.properties?.originFeature && exists) {
        return;
      }
      this._layers[layerId].addFeatureToLayer(feature);
    } catch (error) {
      console.error("Layer Controller", { error, layerId, feature });
      // throw error;
    }
  }
  // Remove feature from layer
  public removeFeature(featureId: string): boolean {
    try {
      for (const layer of Object.values(this._layers)) {
        if (layer.removeFeature(featureId)) return true;
      }
      return false;
    } catch (error) {
      console.error("Layer controller", error);
      throw error;
    }
  }
  // Set selected feature id
  public setSelectedFeatureId(featureId: string | null) {
    Object.values(this._layers).forEach((lay: any) => lay.setSelectedFeatureId(featureId));
  }
  // Set selected feature from feature id
  public setSelectedFeatureFromId(featureId: string) {
    try {
      for (const layer of Object.values(this._layers)) {
        if (layer.setSelectedFeatureFromId(featureId)) return;
      }
    } catch (error) {
      console.error("Layer controller", error);
      throw error;
    }
  }
  // Set previously selected feature
  public setPreviousSelectedFeature(feature: null | any) {
    Object.values(this._layers).forEach((layer) => {
      layer.setPreviousSelectedFeature(feature);
    });
  }
  // Set filter ids
  public setFilterIds(ids: null | string) {
    Object.values(this._layers).forEach((layer) => {
      layer.setFilterIds(ids);
    });
  }
  // Set Transparent mode
  public setTransparentMode(state: boolean) {
    Object.values(this._layers).forEach((layer) => {
      layer.setTransparentMode(state);
    });
  }
  // Set dark mode
  public setDarkTheme(state: boolean) {
    Object.values(this._layers).forEach((layer) => {
      layer.setDarkTheme(state);
    });
  }
  // Set global edit mode
  public setGlobalEditMode(state: boolean) {
    Object.values(this._layers).forEach((layer) => {
      layer.setOptions({ globalEditMode: state });
    });
  }
  // Unbind tooltips
  public unbindTooltips(type: "all" | "points" | "lines" | "polygons" | "labels") {
    Object.values(this._layers).forEach((layer) => {
      layer.unbindTooltipLayer(type);
    });
  }
  // Bind tooltips
  public bindTooltips(type: "all" | "points" | "lines" | "polygons" | "labels") {
    Object.values(this._layers).forEach((layer) => {
      layer.bindTooltipLayer(type);
    });
  }
  // Set translations
  public setTranslations(translations: { [key: string]: string }) {
    Object.values(this._layers).forEach((layer) => {
      layer.setTranslations(translations);
    });
  }
}

export default LayerController;
